/**
 * REPORT-SCREENSHOT-RPS-QUESTIONS.WIDGET.TSX
 */
import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useEffect, useMemo, useState } from "react"
import { DashboardFilters } from "@/redux/filter.types"
import { store } from "@/index"
import { INSUFFICIENT_PARTICIPATION, STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import NoResultsWidget from "./no-results.widget"
import { TopicState } from "@/redux/topic.types"
import { orderBy, snakeCase, sum } from "lodash"
import ListItem from "@/components/list-item"
import Space from "@/components/space"
import getRepartitionColor from "@/utils/get-repartition-color.utils"
import ListCell from "@/components/list-cell"
import Chip from "@/components/chip"
import { fetchQuestions } from "@/redux/_archive.actions"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCommentDots } from "@fortawesome/free-solid-svg-icons"
import { Session } from "@/redux/_session.types"
import PageLoader from "@/components/page-loader"
import { rpsQuestionsGroupForReport } from "@/redux/topic.actions"
import { Page } from "@/redux/page.types"
import { pageInitDashboardFilters } from "@/redux/page.actions"

interface StateProps {
  _session: Session
  topic: TopicState
}

interface OwnProps {
  currentPage: Page
  dashboardFilters: DashboardFilters
  isFullWidth?: boolean
  isReport?: boolean
}

type Props = StateProps & OwnProps & WithTranslation

function ReportScreenshotRpsQuestionsWidget(props: Props) {
  const { t } = props

  const [status, setStatus] = useState(STATUS_LOADING)
  const [questions, setQuestions] = useState<any[]>([])

  useEffect(() => {
    async function load() {
      setStatus(STATUS_LOADING)

      const dashboardFilters = pageInitDashboardFilters(props.currentPage.options.populations)
      const data: any = await store.dispatch(fetchQuestions(dashboardFilters, null, undefined, undefined, true))

      if (data?.error) {
        setStatus(STATUS_LOADED)
        return
      }

      let newQuestions: any[] = []

      props.topic.list.forEach((topic) => {
        topic.Questions.forEach((question: any) => {
          const currentQuestion = data.find((x) => x.aid === question.aid && question.special)

          if (currentQuestion) {
            const repartition = currentQuestion.repartition || []
            const specialAnswer = sum(repartition)

            if (specialAnswer) {
              const negativesAnswers = repartition.length > 2 ? repartition[0] + repartition[1] : repartition[0]

              const specialRate = (negativesAnswers / specialAnswer) * 100

              newQuestions.push({
                id: question.id,
                aid: question.aid,
                label: question.label,
                special: question.special,
                repartition,
                specialAnswer,
                negativesAnswers,
                specialRate
              })
            }
          }
        })
      })

      newQuestions = orderBy(newQuestions, ["specialRate", "repartitionBad"], ["desc", "desc"])

      let countOrder1 = 0
      let countOrder2 = 0

      newQuestions.forEach((question) => {
        if (question.specialRate < 35) {
          question.order = 3
        } else if (
          question.special === "psychological-job-demands" ||
          question.special === "decision-latitude" ||
          question.special === "skill-use"
        ) {
          if (countOrder1 < 3) {
            question.order = 1
            countOrder1++
          } else {
            question.order = 3
          }
        } else {
          if (countOrder2 < 3) {
            question.order = 2
            countOrder2++
          } else {
            question.order = 3
          }
        }
      })

      newQuestions.forEach((question) => {
        if (question.special === "skill-use" || question.special === "decision-latitude") {
          const sameGroup = newQuestions.filter((x) => x.special === "decision-latitude" || x.special === "skill-use")
          question.count = sameGroup.length
          question.index = sameGroup.findIndex((x: any) => x.aid === question.aid) + 1
        } else {
          const sameGroup = newQuestions.filter((x) => x.special === question.special)
          question.count = sameGroup.length
          question.index = sameGroup.findIndex((x: any) => x.aid === question.aid) + 1
        }
      })

      setQuestions(newQuestions)
      setStatus(STATUS_LOADED)
    }

    load()
  }, [props.topic.list, props.dashboardFilters])

  function getQuestionAxis(tag: string) {
    if (tag === "psychological-job-demands") return t("rps_axis_stress")
    if (tag === "skill-use" || tag === "decision-latitude") return t("rps_axis_autonomy")
    if (tag === "team-support" || tag === "manager-support") return t("rps_social_support")
    return t("rps_work_gratitude")
  }

  function getTagColor(tag: string) {
    if (tag === "psychological-job-demands") return "#ff9500"
    if (tag === "decision-latitude" || tag === "skill-use") return "#8bbdd9"
    return null
  }

  const questionGroups = useMemo(() => {
    return rpsQuestionsGroupForReport(props.currentPage, questions)
  }, [props.currentPage, questions])

  const groupIndex = props.currentPage.rpsQuestionsGroupIndex ?? 0
  const visibleQuestions = questionGroups[groupIndex] ?? []

  const visibleOrders = [1, 2, 3].filter((order) => visibleQuestions.some((x) => x.order === order))

  return status === INSUFFICIENT_PARTICIPATION ? (
    <NoResultsWidget />
  ) : (
    <PageLoader status={status}>
      <div
        className="flex1"
        style={{
          transform: props.currentPage.twoColumns ? "scale(0.52)" : "scale(0.62)",
          transformOrigin: "top left",
          width: props.currentPage.twoColumns ? 900 : 1120,
          pointerEvents: "none"
        }}>
        <div className="height-20" />

        {visibleOrders.map((order) => (
          <div key={order}>
            <p className="grey-t">
              <h2>
                <FontAwesomeIcon icon={faCommentDots} />
                &nbsp;
                {t("rps_user_details_" + order)}
              </h2>
            </p>

            <div
              className="flex"
              style={{
                marginTop: "32px",
                marginBottom: "12px"
              }}>
              <div className="flex1 grey-t">
                <b>{t("rps_user_details")}</b>
              </div>

              <ListCell width={210}>
                <b className="grey-t">{t("question_responses")}</b>
              </ListCell>

              <ListCell width={180}>
                <div className="grey-t">
                  <b>{t("axis_rps")}</b>
                </div>
              </ListCell>
            </div>

            {visibleQuestions.filter((x) => x.order === order).length === 0 && (
              <div style={{ margin: "30px 0px 60px 0px" }}>
                <p>
                  <i>{t("rps_user_details_" + order + "_empty")}</i>
                </p>
              </div>
            )}

            <div style={{ margin: "0px -30px 38px -30px" }}>
              {visibleQuestions
                .filter((x) => x.order === order)
                .map((question) => (
                  <ListItem key={question.id} className="py-3">
                    {question.label}

                    <Space />

                    <div className="width-20" />

                    <ListCell width={200}>
                      <div className="flex1">
                        <div className="details-topic-repartition flex flex1">
                          {question.repartition.map((count, i) => (
                            <div
                              key={i}
                              className="details-topic-repartition-item flex"
                              style={{
                                backgroundColor: getRepartitionColor(
                                  i,
                                  question.repartition.length - 1,
                                  true,
                                  props._session.dashboardDisplaySettings,
                                  false
                                ),
                                width: (count / question.specialAnswer) * 100 + "%"
                              }}>
                              <div className="flex1" />
                            </div>
                          ))}
                        </div>

                        <div
                          className="flex grey-t"
                          style={{
                            fontSize: "12px",
                            marginTop: "2px"
                          }}>
                          <Space />
                          {t("rps_responses_negatives", {
                            ratio: question.specialRate.toFixed(1)
                          })}
                        </div>
                      </div>
                    </ListCell>

                    <div className="width-10" />

                    <ListCell width={180}>
                      <div>
                        <div className="flex">
                          <Chip color={getTagColor(question.special)}>
                            {getQuestionAxis(question.special)}&nbsp;({question.index}/{question.count})
                          </Chip>
                        </div>

                        <div className="grey-t" style={{ fontSize: "12px", marginTop: "8px" }}>
                          {t("rps_" + snakeCase(question.special))}
                        </div>
                      </div>
                    </ListCell>
                  </ListItem>
                ))}
            </div>
          </div>
        ))}
      </div>
    </PageLoader>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  topic: state.topic
})

export { ReportScreenshotRpsQuestionsWidget }

export default connect(mapStateToProps)(withTranslation()(ReportScreenshotRpsQuestionsWidget))
